import { useEffect, useState } from 'react';
import type { Task } from '@kolibri/shared';
import { api } from '../../kernel/sync/api';
import { useTabStrip } from '../../kernel/design-system/tab-strip';
import { relativeTime, shortDate } from '../../kernel/design-system/format';
import { useT } from '../../kernel/i18n/i18n';
import { list, useQuery, useRow } from '../../kernel/sync/store';
import { createTask, update } from '../../kernel/sync/mutations';
import { useCanWrite, useFeature, useMe, useMemberMap } from '../../kernel/identity/session';
import { TaskReviewPanel } from '../ai-review/task-review';
import { TaskDecisions } from '../decisions/decision-parts';
import { TaskAgendaOrigin } from '../meetings/meeting-parts';
import { Markdown, MarkdownEditor } from '../pages/Markdown';
import { Comments } from './comments';
import { Relations } from './Relations';
import { TaskTime } from '../time/time';
import { TaskFields } from './fields';
import { Attachments } from '../../kernel/files/Attachments';
import {
  AssigneePicker, CyclePicker, DateField, LabelChips, LabelPicker, ModulePicker, ParentPicker, ParentTrail,
  PriorityPicker, ProjectPicker, StatePicker, stateOf, useDeleteTask,
} from './task-parts';
import { Avatar, Empty, Icon, MenuButton, Sheet, StateDot, useToast } from '../../kernel/design-system/ui';
import { Input, Select } from '../../kernel/design-system/ui/field';
import { Button } from '../../kernel/design-system/ui/button';
import { useMinute } from '../../kernel/design-system/minute';

export function TaskDetail({ taskId, onClose, onOpen }: { taskId: string; onClose: () => void; onOpen: (task: Task) => void }) {
  useMinute(); // re-reads the ages below once a minute — `design-system/minute.ts`
  const time = useFeature('time');
  const canWrite = useCanWrite();
  const t = useT();
  const task = useRow('task', taskId);
  const me = useMe();
  const members = useMemberMap();
  const toast = useToast();
  const removal = useDeleteTask();

  const [title, setTitle] = useState(task?.title ?? '');
  const [editingDescription, setEditingDescription] = useState(false);
  const [description, setDescription] = useState(task?.description ?? '');
  const [tab, setTab] = useState<'comments' | 'activity'>('comments');
  const strip = useTabStrip(tab);
  const [activity, setActivity] = useState<any[]>([]);
  const [newSubtask, setNewSubtask] = useState('');

  const comments = useQuery(
    () => list('comment', (c) => c.task_id === taskId).sort((a, b) => a.created_at - b.created_at),
    [taskId],
  );
  const subtasks = useQuery(() => list('task', (t) => t.parent_id === taskId), [taskId]);
  const state = task ? stateOf(task) : undefined;
  const hasDescription = !!task?.description?.trim();

  useEffect(() => {
    setTitle(task?.title ?? '');
    setDescription(task?.description ?? '');
  }, [taskId, task?.title, task?.description]);

  useEffect(() => {
    if (tab !== 'activity') return;
    api.activity(taskId).then(setActivity).catch(() => setActivity([]));
  }, [tab, taskId]);

  if (!task) {
    return (
      <Sheet title={t('task.title')} onClose={onClose} wide>
        <Empty emoji="🔍" title={t('task.gone')} hint={t('task.goneHint')} />
      </Sheet>
    );
  }

  /** Who filed it — absent for somebody who has since left the workspace. */
  const author = members.get(String(task.created_by ?? ''));

  const saveTitle = () => {
    const next = title.trim();
    if (next && next !== task.title) update('task', task.id, { title: next });
  };

  return (
    <>
      <Sheet
        wide
        onClose={onClose}
        title={
          <span className="flex items-center gap-1.5">
            <StateDot group={state?.group_key} color={state?.color} />
            <span className="mono text-muted">{task.identifier}</span>
          </span>
        }
      >
        {/* Above the title, because "what is this a part of" is read before the
            title rather than after it. */}
        <ParentTrail task={task} onOpen={onOpen} />

        <Input
          style={{ fontSize: 17, fontWeight: 600, border: 'none', padding: '2px 0', marginBottom: 10, background: 'none' }}
          value={title}
          onChange={(event) => setTitle(event.target.value)}
          onBlur={saveTitle}
          onKeyDown={(event) => event.key === 'Enter' && event.currentTarget.blur()}
        />

        <div className="flex items-center flex-wrap gap-1.5 mb-3.5">
          <StatePicker task={task} />
          <PriorityPicker task={task} />
          <AssigneePicker task={task} />
          <LabelPicker task={task} />
          <CyclePicker task={task} />
          <ModulePicker task={task} />
          <ParentPicker task={task} />
          <ProjectPicker task={task} />
          <MenuButton
            variant="ghost" size="sm"
            label={t('common.moreActions')}
            items={[
              { id: 'copy', label: t('action.copyLink'), icon: <Icon name="link" size={14} />, onSelect: () => {
                void navigator.clipboard?.writeText(`${location.origin}/t/${task.id}`);
                toast(t('common.copied'));
              } },
              { id: 'subscribe',
              label: (task.subscribers ?? []).includes(me) ? t('task.unsubscribe') : t('task.subscribe'),
              icon: <Icon name="bell" size={14} />,
              hint: (task.subscribers ?? []).includes(me) ? '✓' : undefined,
              onSelect: () => {
                const current = task.subscribers ?? [];
                update('task', task.id, {
                  subscribers: current.includes(me) ? current.filter((id) => id !== me) : [...current, me],
                });
              } },
            { id: 'archive', label: task.archived ? t('action.unarchive') : t('action.archive'), icon: <Icon name="archive" size={14} />,
                onSelect: () => update('task', task.id, { archived: task.archived ? 0 : 1 }) },
              { id: 'delete', label: t('task.delete'), icon: <Icon name="trash" size={14} />, danger: true, onSelect: async () => {
                if (await removal.ask(task)) onClose();
              } },
            ]}
          >
            <Icon name="dots" size={14} />
          </MenuButton>
        </div>

        {/*
          * Three label-and-control pairs. On a desktop they sit side by side in
          * one line. A phone cannot fit that, and letting them wrap gave three
          * rows each trailing a wide empty gap — the same height as a form and
          * none of the tidiness. So on a phone they *are* a form: one row each,
          * label at the start, control at the end, all three ends aligned.
          */}
        <div className="flex flex-col gap-2 mb-4 sm:flex-row sm:flex-wrap sm:items-center sm:gap-2.5">
          <label className="flex items-center justify-between gap-1.5 text-[12.5px] sm:justify-start">
            <span className="text-muted">{t('task.due')}</span>
            <DateField label={t('task.due')} value={task.due_date} onChange={(value) => update('task', task.id, { due_date: value })} />
          </label>
          <label className="flex items-center justify-between gap-1.5 text-[12.5px] sm:justify-start">
            <span className="text-muted">{t('task.repeats')}</span>
            <Select className="w-[150px] sm:w-[130px]"
              value={task.recurrence ?? ''}
              onChange={(event) => update('task', task.id, { recurrence: event.target.value || null })}
            >
              <option value="">{t('task.repeatsNever')}</option>
              <option value="daily">{t('task.repeatsDaily')}</option>
              <option value="weekly">{t('task.repeatsWeekly')}</option>
              <option value="weekly:2">{t('task.repeatsFortnightly')}</option>
              <option value="monthly">{t('task.repeatsMonthly')}</option>
            </Select>
          </label>
          <label className="flex items-center justify-between gap-1.5 text-[12.5px] sm:justify-start">
            <span className="text-muted">{t('task.estimate')}</span>
            <Input type="number" min={0} step={1} className="w-[150px] sm:w-[84px]"
              value={task.estimate ?? ''}
              onChange={(event) => update('task', task.id, { estimate: event.target.value === '' ? null : Number(event.target.value) })}
            />
          </label>
        </div>

        {time && <TaskTime taskId={task.id} projectId={task.project_id} />}

        <TaskFields task={task} />

        {/* description */}
        <section className="mb-[18px]">
          {/* Reading is what this section is for, so reading is what a click on
              it does. It used to open the editor — which meant a link in a
              description both navigated *and* dropped the editor over the
              screen you had just left, and a checklist could only be read by
              somebody willing to look at the markdown behind it. Editing is a
              button now, the way it is on a page. */}
          <div className="flex items-center gap-2 mb-1.5">
            <strong className="text-[13.5px]">{t('task.description')}</strong>
            <span className="flex-1 min-w-0" />
            {!editingDescription && hasDescription && (
              <Button variant="ghost" size="sm" hidden={!canWrite} onClick={() => setEditingDescription(true)}>
                {t('action.edit')}
              </Button>
            )}
          </div>
          {editingDescription ? (
            <>
              <MarkdownEditor value={description} onChange={setDescription} attachTo={{ kind: 'task', id: task.id }} autoFocus />
              <div className="flex items-center gap-2 mt-2">
                <Button variant="primary" size="sm"
                  onClick={() => {
                    update('task', task.id, { description });
                    setEditingDescription(false);
                  }}
                >
                  {t('action.save')}
                </Button>
                <Button size="sm" onClick={() => { setDescription(task.description ?? ''); setEditingDescription(false); }}>
                  {t('action.cancel')}
                </Button>
              </div>
            </>
          ) : hasDescription ? (
            // A checklist in a description is meant to be ticked off without
            // opening anything, which is what `onChange` is for.
            <Markdown
              source={task.description}
              onChange={(next) => {
                update('task', task.id, { description: next });
                setDescription(next);
              }}
            />
          ) : (
            <Button size="sm" hidden={!canWrite} onClick={() => setEditingDescription(true)}>
              {t('task.addDescription')}
            </Button>
          )}
        </section>

        {/* A review sits under the description, because the description is what
            it mostly has something to say about — and above the sub-tasks,
            because "this is three tasks" is a finding that ends up there. */}
        <TaskReviewPanel task={task} />

        {/* sub-tasks */}
        <section className="mb-[18px]">
          <div className="flex items-center gap-2 mb-1.5">
            <strong className="text-[13.5px]">{t('task.subtasks')}</strong>
            <span className="text-muted text-[12.5px]">
              {t('task.subtaskCount', {
                done: subtasks.filter((child) => stateOf(child)?.group_key === 'completed').length,
                total: subtasks.length,
              })}
            </span>
          </div>
          {subtasks.map((child) => (
            <div key={child.id} className="flex items-center gap-2" style={{ padding: '5px 0', borderTop: '1px solid var(--line)' }}>
              <StateDot group={stateOf(child)?.group_key} color={stateOf(child)?.color} />
              <Button variant="ghost" size="sm" className="flex-1 min-w-0" style={{ justifyContent: 'flex-start' }} onClick={() => onOpen(child)}>
                <span className="mono text-muted">{child.identifier}</span>
                <span className="truncate">{child.title}</span>
              </Button>
            </div>
          ))}
          <form
            className="flex items-center gap-2 mt-2"
           
            onSubmit={(event) => {
              event.preventDefault();
              if (!newSubtask.trim()) return;
              createTask({ project_id: task.project_id, title: newSubtask, parent_id: task.id }, me);
              setNewSubtask('');
            }}
          >
            <Input placeholder={t('task.addSubtask')} value={newSubtask} onChange={(e) => setNewSubtask(e.target.value)} />
            <Button size="sm" type="submit"><Icon name="plus" size={14} /></Button>
          </form>
        </section>

        <Relations task={task} onOpen={onOpen} />

        {/* The paperclip, which used to be written out here and is now the one
            component every screen with files uses. It gained a way to remove
            one in the move: this list had no delete at all, so a screenshot
            attached to the wrong task could only be got rid of over MCP. */}
        <Attachments on={{ kind: 'task', id: task.id }} className="mb-[18px]" />

        {/* What is being decided about this task. Under the sub-tasks and above
            the discussion, because a ballot is a question that has already been
            framed — the comments are where it was still being argued. */}
        <TaskDecisions taskId={task.id} projectId={task.project_id} />

        {/* The meeting it came out of. A task filed in a meeting is a promise
            made in front of people, and the notes around that promise are what
            says what was actually meant by the title. */}
        <TaskAgendaOrigin agendaItemId={task.agenda_item_id} />

        {/* discussion */}
        <section>
          <div ref={strip} className="tabs mb-2.5">
            <button className={tab === 'comments' ? 'active' : ''} onClick={() => setTab('comments')}>
              {t('task.comments')} {comments.length ? `(${comments.length})` : ''}
            </button>
            <button className={tab === 'activity' ? 'active' : ''} onClick={() => setTab('activity')}>{t('task.activity')}</button>
          </div>

          {tab === 'comments' ? (
            <Comments target={{ task_id: task.id }} />
          ) : (
            <div className="flex flex-col gap-2">
              {activity.length === 0 && <span className="text-muted text-[12.5px]">{t('task.noActivity')}</span>}
              {activity.map((entry) => (
                <div key={entry.id} className="flex items-center gap-2 text-[12.5px]" style={{ gap: 7 }}>
                  <Avatar user={members.get(entry.actor_id)} size={18} />
                  <span className="soft flex-1 min-w-0">
                    <strong>{members.get(entry.actor_id)?.name ?? t('common.someone')}</strong>{' '}
                    {entry.verb === 'created' ? t('task.activityCreated')
                      // Both used to fall through to "changed something": the
                      // delete because nothing here asked for it, the restore
                      // because nothing wrote one.
                      : entry.verb === 'deleted' ? t('task.activityDeleted')
                        : entry.verb === 'restored' ? t('task.activityRestored')
                          : t('task.activityChanged', { field: entry.field?.replace('_id', '') ?? t('task.activitySomething') })}
                  </span>
                  <span className="text-muted">{relativeTime(entry.created_at)}</span>
                </div>
              ))}
            </div>
          )}
        </section>

        <div className="text-muted text-[11.5px] mt-[18px]">
          {/* Who filed it, where the line already says when. The trail under
              "Protokoll" has said it all along — behind a tab, in the past
              tense, and only for as long as the entry is still in the last
              fifty. `created_by` is forced on every task the write path makes,
              so this is the same fact without the digging. A creator who has
              left the workspace is not in `members`, and then the line reads
              as it always did rather than "Angelegt von Jemand". */}
          {author
            ? t('task.createdByUpdated', { by: author.name, created: shortDate(task.created_at), updated: relativeTime(task.updated_at) })
            : t('task.createdUpdated', { created: shortDate(task.created_at), updated: relativeTime(task.updated_at) })}
          {task.labels?.length ? <span className="flex items-center gap-2 flex-wrap mt-1.5"><LabelChips ids={task.labels} projectId={task.project_id} /></span> : null}
        </div>
      </Sheet>
      {removal.dialog}
    </>
  );
}
